import * as plausible from '@plausible-analytics/tracker';
import type { PlausibleRequestPayload, PlausibleEventOptions } from '@plausible-analytics/tracker';
import { savedLanguage } from './language';
import type { DelayAmount, DisruptionType } from '../types';

/**
 * Cookieless Plausible analytics (#65). The only module that talks to `@plausible-analytics/tracker`: everything else
 * tracks through `track`, whose types allow only the events below and, for each, only its property keys and values,
 * so personal data or a misspelled event can't compile.
 */

/**
 * The Service Fee delay24 charges on a successful claim, VAT included: 35%, as published in the Price List
 * (`PRICE_LIST` legal document, shown at `/price-list`). It is used here only to report the **potential** fee on a
 * submitted claim as analytics revenue — what a customer is actually charged is decided by the backend and the
 * published document, so changing this number changes the dashboard, never a price.
 */
const SERVICE_FEE_RATE = 0.35;

/** The journey-level disruption as the funnel reports it. */
const DISRUPTION_PROPS = {
  FLIGHT_DELAYED: 'delayed',
  FLIGHT_CANCELED: 'cancelled',
} as const satisfies Record<DisruptionType, string>;

/** The reported delay at the final destination, including the `unknown` a cancelled journey is checked with. */
const DELAY_BRACKET_PROPS = {
  ZERO_TO_TWO_HOURS: '0-2h',
  TWO_TO_THREE_HOURS: '2-3h',
  THREE_TO_FOUR_HOURS: '3-4h',
  FOUR_PLUS_HOURS: '4h+',
  UNKNOWN: 'unknown',
} as const satisfies Record<DelayAmount, string>;

type DisruptionProp = typeof DISRUPTION_PROPS[DisruptionType];
type DelayBracketProp = typeof DELAY_BRACKET_PROPS[DelayAmount];
/**
 * The Journey's Segment count. The values mirror `MAX_SEGMENTS` in `src/types.ts`, which is what stops the form
 * offering a fifth Segment; raise that cap and this union and {@link segmentsProp} must gain the next value with it.
 */
type SegmentsProp = '1' | '2' | '3' | '4';
/** The travelling party's size, every party of five or more sharing one bucket. */
type PassengersProp = '1' | '2' | '3' | '4' | '5+';

/** Each event's properties, `undefined` for an event that takes none. Values are strings, as Plausible expects. */
interface EventProps {
  /** The contact message was accepted. */
  'Contact Sent': undefined;
  /** The Not Found page rendered, for the path it rendered at (redacted). */
  '404': { path: string };
  /** A Co-adult's signing page loaded for a usable Signing link. */
  'Signing Link Opened': undefined;
  /** A Co-adult's signature is in. */
  'Co-adult Signed': undefined;
  /** The signing page shows a Signing link that can't be used, whether found so on loading, signing or resending. */
  'Signing Link Unusable': { reason: 'expired' | 'used' | 'unknown' };
  /** A fresh Signing link was emailed in place of an expired one. */
  'Signing Link Resent': undefined;
  /** The claim form opened. The top of the claim funnel: everyone who tried. */
  'Claim Started': undefined;
  /** The eligibility check answered with a verdict — any verdict. A check that ends in an error reports nothing. */
  'Eligibility Checked': {
    result: 'eligible' | 'ineligible' | 'not_pursuable';
    disruption: DisruptionProp;
    delay_bracket: DelayBracketProp;
    segments: SegmentsProp;
  };
  /** The verdict was eligible *and* pursuable, for the Estimate's Compensation tier. */
  'Eligible': { amount: string };
  /** The claimant confirmed the resolved Journey and moved on. */
  'Flight Confirmed': undefined;
  /** The travelling party passed validation and the form moved on. */
  'Passengers Completed': { passengers: PassengersProp };
  /** The Lead passenger signed and moved on. */
  'Claim Signed': undefined;
  /** The claim was submitted successfully. Carries the potential fee as revenue; see {@link trackClaimSubmitted}. */
  'Claim Submitted': {
    disruption: DisruptionProp;
    segments: SegmentsProp;
    amount: string;
    passengers: PassengersProp;
  };
}

export type AnalyticsEvent = keyof EventProps;

type TrackArgs<E extends AnalyticsEvent> = EventProps[E] extends undefined ? [event: E] : [event: E, props: EventProps[E]];

let initialised = false;

/** Whether the visitor objects to analytics through Global Privacy Control, as Privacy Policy v1.1 tells them to. */
const globalPrivacyControlOn = () =>
  (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;

/**
 * Rewrite a Signing link's secret token to `_token_`, keeping everything around it, so no token leaves the site. The
 * pattern mirrors the `/sign/:token` route in `App.tsx`; change both together.
 */
const redactSigningLinkToken = (url: string) => url.replace(/\/sign\/[^/?#]+/, '/sign/_token_');

/**
 * Start analytics, once at app start. Does nothing unless the build sets `REACT_APP_PLAUSIBLE_DOMAIN`, or when the
 * visitor has Global Privacy Control on. Plausible's own opt-out isn't used: it relies on `localStorage`.
 */
export function initAnalytics(): void {
  const domain = process.env.REACT_APP_PLAUSIBLE_DOMAIN;
  if (!domain || globalPrivacyControlOn()) return;
  try {
    plausible.init({
      domain,
      autoCapturePageviews: true,
      outboundLinks: true,
      fileDownloads: false,
      formSubmissions: false,
      captureOnLocalhost: false,
      customProperties: () => ({ language: savedLanguage() }),
      transformRequest: (payload: PlausibleRequestPayload) => ({ ...payload, u: redactSigningLinkToken(payload.u) }),
    });
    initialised = true;
  } catch {
    // Analytics must never break the page.
  }
}

/** Send an event, or nothing when analytics is off. Never throws, even if Plausible is blocked or unreachable. */
export function track<E extends AnalyticsEvent>(...[event, props]: TrackArgs<E>): void {
  send(event, toOptions(props));
}

/** The one call into the tracker: every event, with or without revenue, leaves through here. */
function send(event: AnalyticsEvent, options: PlausibleEventOptions): void {
  if (!initialised) return;
  try {
    plausible.track(event, options);
  } catch {
    // Analytics must never break the page.
  }
}

/**
 * Report a submitted claim with its potential fee as revenue: the Estimate for every passenger on the claim, at
 * {@link SERVICE_FEE_RATE}. It is an upper bound on fee pipeline, not realised revenue — the Estimate is the
 * Compensation tier ceiling before assessment, and not every submitted claim is paid.
 *
 * Kept here, rather than at the call site, so the fee rate, the rounding and the bucketing of a missing Estimate can't
 * be got wrong twice: an Estimate the check never sent reports `amount: unknown` and simply no revenue, exactly as
 * `Eligible` reports it, and the funnel's bottom step still fires — it must match the claims that were really filed.
 */
export function trackClaimSubmitted(claim: {
  amount: number | null;
  currency: string | null;
  passengers: number;
  disruption: DisruptionType;
  segments: number;
}): void {
  const { amount, currency, passengers } = claim;
  // Typed as this event's own props, not left to `toOptions`, which takes every event's props as a union: without the
  // annotation a missing or misspelled key here would still compile if it happened to fit some other event's shape.
  const props: EventProps['Claim Submitted'] = {
    disruption: disruptionProp(claim.disruption),
    segments: segmentsProp(claim.segments),
    amount: amountProp(amount),
    passengers: passengersProp(passengers),
  };
  // Rounded to the currency's minor unit: `600 * 2 * 0.35` is 420.00000000000006 in binary floating point.
  const fee = amount === null ? null : Number((amount * passengers * SERVICE_FEE_RATE).toFixed(2));
  send('Claim Submitted', {
    ...toOptions(props),
    ...(fee !== null && currency ? { revenue: { amount: fee, currency } } : {}),
  });
}

/** The journey-level disruption as the funnel reports it. */
export const disruptionProp = (disruption: DisruptionType): DisruptionProp => DISRUPTION_PROPS[disruption];

/**
 * The delay bracket as the funnel reports it. Pass the bracket the **check was asked with**, not the form field: a
 * cancelled journey is checked as `UNKNOWN` while a bracket picked before the switch stays behind in form state.
 */
export const delayBracketProp = (delay: DelayAmount): DelayBracketProp => DELAY_BRACKET_PROPS[delay];

/** The Journey's Segment count as the funnel reports it. */
export const segmentsProp = (count: number): SegmentsProp =>
  count >= 4 ? '4' : count >= 3 ? '3' : count >= 2 ? '2' : '1';

/** The travelling party's size as the funnel reports it, every party of five or more sharing one bucket. */
export const passengersProp = (count: number): PassengersProp =>
  count >= 5 ? '5+' : count >= 4 ? '4' : count >= 3 ? '3' : count >= 2 ? '2' : '1';

/**
 * The Estimate's Compensation tier as a property value. Not a fixed set: SHY-YOLCU has a domestic tier, so whatever
 * the check returns is reported. A verdict that carried no Estimate reads `unknown` rather than dropping the event.
 */
export const amountProp = (amount: number | null | undefined): string =>
  amount === null || amount === undefined ? 'unknown' : String(amount);

/** The tracker options for an event's props, each value redacted like every request's URL. */
function toOptions(props: EventProps[AnalyticsEvent]): { props?: Record<string, string> } {
  if (!props) return {};
  return {
    props: Object.fromEntries(Object.entries(props).map(([key, value]) => [key, redactSigningLinkToken(value)])),
  };
}
